import { FormatListBulleted } from '@mui/icons-material';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import { Box, IconButton, MenuItem, Popover, Select, Stack, CircularProgress, useTheme } from '@mui/material';
import React, { forwardRef, Suspense, useRef, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Quill from 'quill';

// Register custom attributes for text color and background
const Parchment = Quill.import('parchment');

// Create custom attributes
const ColorClassAttribute = new Parchment.Attributor.Class('color-class', 'ql-color', {
    scope: Parchment.Scope.INLINE
});

const BackgroundClassAttribute = new Parchment.Attributor.Class('background-class', 'ql-bg', {
    scope: Parchment.Scope.INLINE
});

// Register the attributes
Quill.register(ColorClassAttribute);
Quill.register(BackgroundClassAttribute);

type FileUploadHandler = (files: FileList) => Promise<any[]>;

type CustomQuillEditorProps = {
    value: string;
    uploadProgress: number;
    onChange: (value: string) => void;
    placeholder?: string;
    onUploadFiles?: FileUploadHandler;
};

const BlogHtmlEditor = forwardRef<ReactQuill, CustomQuillEditorProps>(({
    value,
    onChange,
    placeholder = 'Écrivez ici...',
    onUploadFiles,
    uploadProgress,
}, ref) => {

    const quillRef = useRef<ReactQuill | null>(null);
    // Allow parent to access the quillRef if needed
    React.useImperativeHandle(ref, () => quillRef.current as ReactQuill);

    // Add loading state for file uploads
    const [uploadingVideo, setUploadingVideo] = useState(false)
    const [uploadingImage, setUploadingImage] = useState(false)

    const TEXT_COLORS = [
        { label: 'Default', value: 'ql-color-default', style: {} },
        { label: 'Purple', value: 'ql-color-purple', style: { color: '#4239B0' } },
        { label: 'Teal', value: 'ql-color-teal', style: { color: '#118999' } },
        { label: 'Yellow', value: 'ql-color-yellow', style: { color: '#F2D74A' } },
        { label: 'Pink', value: 'ql-color-pink', style: { color: '#EA8080' } },
        { label: 'Blue', value: 'ql-color-blue', style: { color: '#2F80ED' } },
    ];
    const HIGHLIGHT_COLORS = [
        { label: 'None', value: 'ql-bg-none', style: { background: (theme) => theme.palette.mode === 'dark' ? 'none' : 'none' } },
        { label: 'Purple', value: 'ql-bg-purple', style: { background: (theme) => theme.palette.mode === 'dark' ? '#262738' : '#F2F1FF' } },
        { label: 'Green', value: 'ql-bg-green', style: { background: (theme) => theme.palette.mode === 'dark' ? '#1F3838' : '#F4FBF4' } },
        { label: 'Yellow', value: 'ql-bg-yellow', style: { background: (theme) => theme.palette.mode === 'dark' ? '#45422C' : '#FEF9ED' } },
        { label: 'Pink', value: 'ql-bg-pink', style: { background: (theme) => theme.palette.mode === 'dark' ? '#40292F' : '#FCEEEE' } },
        { label: 'Blue', value: 'ql-bg-blue', style: { background: (theme) => theme.palette.mode === 'dark' ? '#243448' : '#F3F8FF' } },
    ];

    const theme = useTheme();

    // Popover state
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const handleOpenPopover = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClosePopover = () => {
        setAnchorEl(null);
    };
    const handleTextColor = (className: string | boolean) => {
        const editor = quillRef.current?.getEditor();
        if (!editor) return;
        const range = editor.getSelection();
        if (!range) return;

        if (!className || className === 'ql-color-default') {
            // Remove color class
            editor.format('color-class', false);
        } else {
            // Extract class name without prefix
            const colorClass = typeof className === 'string' ? className.replace('ql-color-', '') : '';
            editor.format('color-class', colorClass);
        }
        handleClosePopover();
    };

    const handleHighlight = (className: string | boolean) => {
        const editor = quillRef.current?.getEditor();
        if (!editor) return;
        const range = editor.getSelection();
        if (!range) return;

        if (!className || className === 'ql-bg-none') {
            // Remove background class
            editor.format('background-class', false);
        } else {
            // Extract class name without prefix
            const bgClass = typeof className === 'string' ? className.replace('ql-bg-', '') : '';
            editor.format('background-class', bgClass);
        }
        handleClosePopover();
    };

    return (
        <Suspense fallback={<div>Loading...</div>}>
            <Box
                id="html-editor"
                component={ReactQuill}
                ref={quillRef}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                modules={{
                    toolbar: {
                        container: []
                    }
                }}
                formats={[
                    'header', 'bold', 'italic', 'underline', 'strike', 'blockquote',
                    'list', 'bullet', 'link', 'image', 'video', 'color-class', 'background-class'
                ]}
                sx={{
                    "& .ql-snow": {
                        "& .ql-editor": {
                            padding: 0,
                            "& p, & p *": {
                                color: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#4e4e4e',
                            },
                            "& h1, & h1 *": {
                                color: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#4e4e4e',
                                fontFamily: "var(--font-family-Core, Plus Jakarta Sans)",
                                fontStyle: "normal",
                                fontWeight: 700,
                                lineHeight: { xs: "40px", md: "48px", lg: "56px" },
                                fontSize: { xs: 24, md: 40, lg: 48 },
                                marginBottom: { xs: 2, md: 3, lg: 4 },
                            },
                            "& h2, & h2 *": {
                                color: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#4e4e4e',
                                fontFamily: "var(--font-family-Core, Plus Jakarta Sans)",
                                fontStyle: "normal",
                                fontWeight: 700,
                                fontSize: { xs: 20, md: 32, lg: 36 },
                                marginBottom: { xs: 2, md: 3, lg: 4 },
                            },
                            "& h3, & h3 *": {
                                color: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#4e4e4e',
                                fontFamily: "var(--font-family-Core, Plus Jakarta Sans)",
                                fontStyle: "normal",
                                fontWeight: 700,
                                fontSize: { xs: 18, md: 24, lg: 28 },
                                marginBottom: { xs: 2, md: 3, lg: 4 },
                            },
                            '& ul': { pl: 3, mb: 2, color: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#4e4e4e', },
                            '& li': { mb: 1, color: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#4e4e4e', },
                            '& blockquote': {
                                color: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#4e4e4e',
                                borderLeft: '9px solid #4239B0',
                                p: 2,
                                my: 3,
                                fontFamily: "var(--font-family-Core, Plus Jakarta Sans)",
                                fontSize: { xs: 16, md: 20 },
                                fontStyle: "italic",
                                fontWeight: 500,
                                lineHeight: "var(--font-line-height-md, 32px)",
                            },
                            '& img': {
                                display: "block",
                                width: '80%',
                                borderRadius: 2,
                                my: 2,
                                mx: "auto",
                                objectFit: 'cover',
                                maxHeight: 500,
                            },
                            '& .ql-video, video': {
                                display: "block",
                                width: '80%',
                                borderRadius: '16px',
                                my: 2,
                                mx: "auto",
                                aspectRatio: '16/9'
                            },
                            "& strong *": {
                                color: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#4e4e4e',
                                fontWeight: "700 !important",
                            },

                            "& p, li, s, em": {
                                color: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#4e4e4e',
                                fontFamily: "var(--font-family-Core, Plus Jakarta Sans)",
                                fontWeight: 500,
                                margin: "0 0 16px 0",
                                fontSize: { xs: 16, md: 20 },
                                lineHeight: { xs: "24px", md: "32px" },
                                textAlign: "left",
                            },
                            // Remove margin-top for the first element in the article content
                            "& > *:first-of-type": {
                                marginTop: 0,
                            },
                            "& a": {
                                color: "#4239B0",
                                fontFamily: "var(--font-family-Core, Plus Jakarta Sans)",
                                textDecorationLine: "underline",
                                textDecorationStyle: "solid",
                                textDecorationSkipInk: "auto",
                                textDecorationThickness: "auto",
                                textUnderlineOffset: "auto",
                                textUnderlinePosition: "from-font",
                            },

                            "& em": {
                                color: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#4e4e4e',
                                fontStyle: "italic !important",
                            }
                        },
                        // Custom color classes for text colors (support theme.mode)
                        '& .ql-color-default': {
                            color: (theme) => theme.palette.mode === 'dark' ? 'inherit !important' : 'inherit !important',
                            fontWeight: 500
                        },
                        '& .ql-color-purple': {
                            color: (theme) => theme.palette.mode === 'dark' ? '#4239B0 !important' : '#4239B0 !important',
                            fontWeight: 500
                        },
                        '& .ql-color-teal': {
                            color: (theme) => theme.palette.mode === 'dark' ? '#118999 !important' : '#118999 !important',
                            fontWeight: 500
                        },
                        '& .ql-color-yellow': {
                            color: (theme) => theme.palette.mode === 'dark' ? '#F2D74A !important' : '#F2D74A !important',
                            fontWeight: 500
                        },
                        '& .ql-color-pink': {
                            color: (theme) => theme.palette.mode === 'dark' ? '#EA8080 !important' : '#EA8080 !important',
                            fontWeight: 500
                        },
                        '& .ql-color-blue': {
                            color: (theme) => theme.palette.mode === 'dark' ? '#2F80ED !important' : '#2F80ED !important',
                            fontWeight: 500
                        },
                        // Custom background classes for highlights (support theme.mode)
                        '& .ql-bg-none': {
                            background: (theme) => theme.palette.mode === 'dark' ? 'none !important' : 'none !important',
                            fontWeight: 500
                        },
                        '& .ql-bg-purple': {
                            background: (theme) => theme.palette.mode === 'dark' ? '#262738 !important' : '#F2F1FF !important',
                            fontWeight: 500
                        },
                        '& .ql-bg-green': {
                            background: (theme) => theme.palette.mode === 'dark' ? '#1F3838 !important' : '#F4FBF4 !important',
                            fontWeight: 500
                        },
                        '& .ql-bg-yellow': {
                            background: (theme) => theme.palette.mode === 'dark' ? '#45422C !important' : '#FEF9ED !important',
                            fontWeight: 500
                        },
                        '& .ql-bg-pink': {
                            background: (theme) => theme.palette.mode === 'dark' ? '#40292F !important' : '#FCEEEE !important',
                            fontWeight: 500
                        },
                        '& .ql-bg-blue': {
                            background: (theme) => theme.palette.mode === 'dark' ? '#243448 !important' : '#F3F8FF !important',
                            fontWeight: 500
                        },
                    },

                    "& .ql-blank": {
                        "&:before": {
                            color: "var(--text-Disabled, #939393)",
                            fontFamily: "var(--font-family-Core, Plus Jakarta Sans)",
                            fontSize: { xs: 16, md: 20 },
                            fontStyle: "normal",
                            fontWeight: 700,
                        },
                    },
                    "& .ql-toolbar": {
                        display: "none"
                    },
                    "& .ql-container": {
                        border: "none"
                    },
                    '& .ql-tooltip': {
                        '& .ql-preview': {
                            color: "#4239B0",
                            fontSize: 16,
                            lineHeight: "24px",
                        },
                        '& .ql-action': {
                            color: "#4239B0",
                            fontSize: 16,
                        },
                        '& .ql-remove': {
                            color: "red",
                            fontSize: 16,
                        },
                    },
                    // placeholder styling
                    "& .ql-editor.ql-blank::before": {
                        right: 0,
                        left: 0
                    },
                }}
            />
            <Stack sx={{
                position: "fixed",
                bottom: 8,
                left: 0,
                justifyContent: "center",
                alignItems: "center",
                width: "100%"
            }}>
                <Box sx={{
                    margin: "0 auto",
                    borderRadius: "16px",
                    display: "inline-flex",
                    padding: "12px",
                    flexDirection: "row",
                    alignItems: "flex-start",
                    gap: "12px",
                    border: "2px solid rgba(255, 255, 255, 0.30)",
                    background: (theme) => theme.palette.mode === 'dark' ? "var(--Dasbhoard, #222426)" : "var(--Neutral-White, #ffffff)",
                    boxShadow: "0px 4px 80px 0px rgba(112, 118, 176, 0.33)",
                    "& > *:not(:last-child)": {
                        borderRight: "2px dashed #FFF",
                        paddingRight: "12px",
                    },
                }}>
                    <Stack gap={1} flexDirection={'row'}>
                        <IconButton
                            size="small"
                            type="button"
                            onClick={() => {
                                const editor = quillRef.current?.getEditor();
                                if (!editor) return;
                                // @ts-ignore
                                editor.history.undo();
                            }}
                            aria-label="Undo"
                        >
                            <i className="fi fi-br-undo"></i>
                        </IconButton>
                        <IconButton
                            size="small"
                            type="button"
                            onClick={() => {
                                const editor = quillRef.current?.getEditor();
                                if (!editor) return;
                                // @ts-ignore
                                editor.history.redo();
                            }}
                            aria-label="Redo"
                        >
                            <i className="fi fi-br-redo"></i>
                        </IconButton>
                    </Stack>
                    <Stack gap={1} flexDirection={'row'}>
                        <Select
                            size="small"
                            defaultValue="p"
                            value={
                                (() => {
                                    const editor = quillRef.current?.getEditor();
                                    if (!editor) return '';
                                    const format = editor.getFormat();
                                    if (format.header === 1) return 'h1';
                                    if (format.header === 2) return 'h2';
                                    if (format.header === 3) return 'h3';
                                    return 'p';
                                })()
                            }
                            onChange={e => {
                                const value = e.target.value;
                                const editor = quillRef.current?.getEditor();
                                if (!editor) return;
                                if (value === 'h1') editor.format('header', 1);
                                else if (value === 'h2') editor.format('header', 2);
                                else if (value === 'h3') editor.format('header', 3);
                                else editor.format('header', false);
                            }}
                            displayEmpty
                            sx={{
                                color: (theme) => theme.palette.mode === 'dark' ? "#FFFFFF" : "#939393",
                                minWidth: 80, fontWeight: 700,
                                "& fieldset": {
                                    "border": "none"
                                },
                                "& .MuiOutlinedInput-input": {
                                    padding: '4px'
                                }
                            }}
                            inputProps={{
                                'aria-label': 'Header Level',
                            }}
                        >
                            <MenuItem value="h1" style={{ fontWeight: 700, fontSize: 24 }}>Heading 1</MenuItem>
                            <MenuItem value="h2" style={{ fontWeight: 700, fontSize: 20 }}>Heading 2</MenuItem>
                            <MenuItem value="h3" style={{ fontWeight: 700, fontSize: 18 }}>Heading 3</MenuItem>
                            <MenuItem value="p" style={{ fontWeight: 400, fontSize: 14 }}>Paragraph</MenuItem>
                        </Select>
                        <IconButton
                            size="small"
                            type="button"
                            onClick={handleOpenPopover}
                            aria-label="Text Color & Highlight"
                            sx={{
                                borderRadius: "12px",
                                border: "1px solid var(--border-border-primary, #FFF)",
                                width: 32,
                                height: 32,
                            }}
                        >
                            <i className="fi fi-br-letter-case" style={{ marginTop: 5 }} />
                        </IconButton>
                        <Popover
                            open={Boolean(anchorEl)}
                            anchorEl={anchorEl}
                            onClose={handleClosePopover}
                            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                            transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                            slotProps={{
                                paper: {
                                    sx: {
                                        borderRadius: "16px",
                                        border: "1px solid rgba(255, 255, 255, 0.10)",
                                        background: (theme) => theme.palette.mode === 'dark' ? "var(--background-Primary, #222426)" : "var(--Neutral-White, #ffffff)",
                                        boxShadow: "0px 4px 80px 0px rgba(112, 144, 176, 0.14)",
                                        display: "inline-flex",
                                        padding: "12px",
                                        flexDirection: "column",
                                        alignItems: "flex-start",
                                    }
                                }
                            }}
                            sx={{
                                mt: -2, // extra margin to move popover up
                            }}
                        >
                            <div style={{ marginBottom: 8, fontWeight: 600, fontSize: 16, color: theme.palette.mode === 'dark' ? "#CBCBCB" : "#939393" }}>Couleurs du texte</div>
                            <Stack direction="row" spacing={'12px'} mb={2}>
                                {TEXT_COLORS.map(opt => (
                                    <IconButton
                                        key={opt.label}
                                        size="small"
                                        onClick={() => handleTextColor(opt.value)}
                                        style={{
                                            border: '1px solid #7C798B',
                                            borderRadius: 8,
                                            width: 32, height: 32, display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            ...opt.style,
                                        }}
                                    >
                                        <span style={{ fontWeight: 700, marginTop: 5 }}><i className="fi fi-br-letter-case" /></span>
                                    </IconButton>
                                ))}
                            </Stack>
                            <div style={{ marginBottom: 8, fontWeight: 600, fontSize: 16, color: theme.palette.mode === 'dark' ? "#CBCBCB" : "#939393" }}>Surlignages du texte</div>
                            <Stack direction="row" spacing={'12px'}>
                                {HIGHLIGHT_COLORS.map(opt => (
                                    <IconButton
                                        key={String(opt.value)}
                                        size="small"
                                        onClick={() => handleHighlight(opt.value)}
                                        sx={{
                                            border: '1px solid #7C798B',
                                            width: 32, height: 32, display: 'flex', justifyContent: 'center', alignItems: 'center',
                                            ...opt.style,
                                        }}
                                    >
                                        {opt.value === 'ql-bg-none' ? <i className="fi fi-br-ban" style={{ marginTop: 5 }}></i> : <span>&nbsp;</span>}
                                    </IconButton>
                                ))}
                            </Stack>
                        </Popover>
                    </Stack>
                    <Stack gap={1} flexDirection={'row'}>
                        <IconButton
                            size="small"
                            type="button"
                            onClick={() => {
                                const editor = quillRef.current?.getEditor();
                                if (!editor) return;
                                const format = editor.getFormat();
                                editor.format('bold', !format.bold);
                            }}
                            style={{ fontWeight: 'bold', marginRight: 8 }}
                            aria-label="Bold"
                        >
                            <i className="fi fi-br-bold"></i>
                        </IconButton>
                        <IconButton
                            size="small"
                            type="button"
                            onClick={() => {
                                const editor = quillRef.current?.getEditor();
                                if (!editor) return;
                                const format = editor.getFormat();
                                editor.format('italic', !format.italic);
                            }}
                            style={{ fontStyle: 'italic', marginRight: 8 }}
                            aria-label="Italic"
                        >
                            <i className="fi fi-br-italic"></i>
                        </IconButton>
                        <IconButton
                            size="small"
                            type="button"
                            onClick={() => {
                                const editor = quillRef.current?.getEditor();
                                if (!editor) return;
                                const format = editor.getFormat();
                                editor.format('strike', !format.strike);
                            }}
                            style={{ textDecoration: 'line-through' }}
                            aria-label="Strikethrough"
                        >
                            <i className="fi fi-br-strikethrough"></i>
                        </IconButton>
                        <IconButton
                            size="small"
                            type="button"
                            onClick={() => {
                                const editor = quillRef.current?.getEditor();
                                if (!editor) return;
                                const format = editor.getFormat();
                                editor.format('underline', !format.underline);
                            }}
                            aria-label="Underline"
                        >
                            <i className="fi fi-br-underline"></i>
                        </IconButton>
                        <IconButton
                            size="small"
                            type="button"
                            onClick={() => {
                                const editor = quillRef.current?.getEditor();
                                if (!editor) return;
                                const range = editor.getSelection();
                                if (range) {
                                    const url = window.prompt('Enter the URL');
                                    if (url) {
                                        editor.format('link', url);
                                    }
                                }
                            }}
                            aria-label="Add Link"
                        >
                            <i className="fi fi-br-link"></i>
                        </IconButton>
                    </Stack>
                    <Stack gap={1} flexDirection={'row'}>
                        <IconButton
                            size="small"
                            type="button"
                            onClick={async () => {
                                const editor = quillRef.current?.getEditor();
                                if (!editor) return;
                                const input = document.createElement('input');
                                input.setAttribute('type', 'file');
                                input.setAttribute('accept', 'image/*');
                                input.onchange = async (event: any) => {
                                    if (!onUploadFiles) return;
                                    const files = event.target.files;
                                    const range = editor.getSelection(true);
                                    let loadingIndex = range ? range.index : 0;
                                    // Show progress in placeholder
                                    let placeholder = `[Téléchargement en cours...]`;
                                    editor.insertText(loadingIndex, placeholder);
                                    editor.setSelection(loadingIndex + placeholder.length, 0);
                                    setUploadingImage(true); // Start spinner
                                    try {
                                        const result = await onUploadFiles(files);
                                        if (result && Array.isArray(result)) {
                                            editor.deleteText(loadingIndex, placeholder.length);
                                            let insertIndex = loadingIndex;
                                            result.forEach((file: any) => {
                                                if (!file) return;
                                                editor.insertEmbed(insertIndex, 'image', file.url);
                                                insertIndex += 1;
                                            });
                                            editor.setSelection(insertIndex, 0);
                                        }
                                    } catch {
                                        editor.deleteText(loadingIndex, placeholder.length);
                                    }
                                    setUploadingImage(false); // Stop spinner
                                };
                                input.click();
                            }}
                            aria-label="Insert Image"
                        >
                            {uploadingImage ? (
                                <Stack sx={{ position: 'relativ', display: 'flex', alignItems: 'center', color: '#fff' }}>
                                    <CircularProgress
                                        size={24}
                                        variant={uploadProgress < 100 ? 'determinate' : 'indeterminate'}
                                        value={uploadProgress}
                                        sx={{ color: '#fff', mr: 1 }}
                                    />
                                    <span style={{ position: 'absolute', color: '#fff', fontSize: 12, top: 10, left: 1 }}>{uploadProgress}%</span>
                                </Stack>
                            ) : <i className="fi fi-br-picture" style={{ color: '#fff' }}></i>}
                        </IconButton>
                        <IconButton
                            size="small"
                            type="button"
                            onClick={async () => {
                                const editor = quillRef.current?.getEditor();
                                if (!editor) return;
                                const input = document.createElement('input');
                                input.setAttribute('type', 'file');
                                input.setAttribute('accept', 'video/mp4'); // Only allow mp4
                                input.onchange = async (event: any) => {
                                    if (!onUploadFiles) return;
                                    const files = event.target.files;
                                    // Filter to only allow mp4 files
                                    const mp4Files = Array.from(files).filter((file: File) => file.type === 'video/mp4');
                                    if (mp4Files.length === 0) {
                                        alert('Please select an MP4 video file.');
                                        return;
                                    }
                                    const range = editor.getSelection(true);
                                    let loadingIndex = range ? range.index : 0;
                                    // Show progress in placeholder
                                    let placeholder = `[Téléchargement en cours...]`;
                                    editor.insertText(loadingIndex, placeholder);
                                    editor.setSelection(loadingIndex + placeholder.length, 0);
                                    setUploadingVideo(true); // Start spinner
                                    try {
                                        const result = await onUploadFiles(mp4Files as any);
                                        if (result && Array.isArray(result)) {
                                            editor.deleteText(loadingIndex, placeholder.length);
                                            let insertIndex = loadingIndex;
                                            result.forEach((file: any) => {
                                                if (!file) return;
                                                editor.insertEmbed(insertIndex, 'video', file.url);
                                                insertIndex += 1;
                                            });
                                            editor.setSelection(insertIndex, 0);
                                        }
                                    } catch {
                                        editor.deleteText(loadingIndex, placeholder.length);
                                    }
                                    setUploadingVideo(false); // Stop spinner
                                };
                                input.click();
                            }}
                            aria-label="Insert Video"
                        >
                            {uploadingVideo ? (
                                <Stack sx={{ position: 'relativ', display: 'flex', alignItems: 'center', color: '#fff' }}>
                                    <CircularProgress
                                        size={24}
                                        variant={uploadProgress < 100 ? 'determinate' : 'indeterminate'}
                                        value={uploadProgress}
                                        sx={{ color: '#fff', mr: 1 }}
                                    />
                                    <span style={{ position: 'absolute', color: '#fff', fontSize: 12, top: 10, left: 1 }}>{uploadProgress}%</span>
                                </Stack>
                            ) : <i className="fi fi-br-play-alt"></i>}
                        </IconButton>
                        <IconButton
                            size="small"
                            type="button"
                            onClick={() => {
                                const editor = quillRef.current?.getEditor();
                                if (!editor) return;
                                const format = editor.getFormat();
                                editor.format('blockquote', !format.blockquote);
                            }}
                            aria-label="Blockquote"
                        >
                            <i className="fi fi-br-quote-right"></i>
                        </IconButton>
                    </Stack>
                    <Stack gap={1} flexDirection={'row'}>
                        <IconButton
                            size="small"
                            type="button"
                            onClick={() => {
                                const editor = quillRef.current?.getEditor();
                                if (!editor) return;
                                editor.removeFormat(editor.getSelection()?.index || 0, editor.getSelection()?.length || 0);
                            }}
                            aria-label="Clear Formatting"
                        >
                            <i className="fi fi-br-eraser"></i>
                        </IconButton>
                    </Stack>
                    <Stack gap={1} flexDirection={'row'}>
                        <IconButton
                            size="small"
                            type="button"
                            onClick={() => {
                                const editor = quillRef.current?.getEditor();
                                if (!editor) return;
                                const format = editor.getFormat();
                                editor.format('list', format.list === 'bullet' ? false : 'bullet');
                            }}
                            aria-label="Bullet List"
                        >
                            <FormatListBulleted />
                        </IconButton>
                        <IconButton
                            size="small"
                            type="button"
                            onClick={() => {
                                const editor = quillRef.current?.getEditor();
                                if (!editor) return;
                                const format = editor.getFormat();
                                editor.format('list', format.list === 'ordered' ? false : 'ordered');
                            }}
                            aria-label="Numbered List"
                        >
                            <FormatListNumberedIcon />
                        </IconButton>
                    </Stack>
                </Box>
            </Stack>
        </Suspense>
    );
});

export default BlogHtmlEditor;
